import React from 'react';
import {
  BankAccountInfiniteSelectField,
  DocumentTypesInfiniteSelectField,
  InfiniteTable,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import { PurchaseFinancialInstallmentList } from '@/pages/MovementsPage/PurchasesPage/ReleasesPage/PurchaseDetailsPage/PurchaseFinancialPage';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Input } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { Moment } from 'moment';
import { FaExchangeAlt } from 'react-icons/fa';
import DocumentTypeColumnFilter from '@/components/_common/DocumentType/DocumentTypeColumnFilter';
import BankAccountColumnFilter from '@/components/_common/BankAccount/BankAccountColumnFilter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useReleasePurchaseFormContext } from '@/pages/MovementsPage/PurchasesPage/ReleasesPage/ReleasesPurchaseProvider';
import { DocumentType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import { useDisclosure } from '@/hooks';

type Props = {
  installments: PurchaseFinancialInstallmentList[];
  setInstallments: React.Dispatch<
    React.SetStateAction<PurchaseFinancialInstallmentList[]>
  >;
};

const PurchaseFinancialInstallments: React.FC<Props> = ({
  installments,
  setInstallments,
}) => {
  const { purchaseReadOnly } = useReleasePurchaseFormContext();
  const [filterDocTypeDropdownOpened, filterDocTypeDropdownActions] =
    useDisclosure();
  const [filterBankAccountDropdownOpened, filterBankAccountDropdownActions] =
    useDisclosure();

  function setFieldValue([field, index]: [string, number], value: unknown) {
    setInstallments((state) =>
      state.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  }

  function setDocumentTypeId(
    documentTypeId: number,
    documentType: DocumentType,
  ) {
    setInstallments((prev) =>
      prev.map((item) => ({
        ...item,
        documentTypeId,
        bankAccountId: documentType?.bankAccountId || item.bankAccountId,
      })),
    );

    filterDocTypeDropdownActions.close();
  }

  function setBankAccountId(bankAccountId: number) {
    setInstallments((prev) => prev.map((item) => ({ ...item, bankAccountId })));

    filterBankAccountDropdownActions.close();
  }

  const columns: ColumnsType<PurchaseFinancialInstallmentList> = [
    {
      dataIndex: 'description',
      title: 'Descrição',
      width: COLUMNS_WIDTH.default,
      render: (value: string, _, i) => (
        <Input
          value={value}
          onChange={({ target }) =>
            setFieldValue(['description', i], target.value)
          }
          disabled={purchaseReadOnly}
        />
      ),
    },
    {
      dataIndex: 'documentNumber',
      title: 'Número do documento',
      width: COLUMNS_WIDTH.default,
      render: (value: string, _, i) => (
        <Input
          value={value}
          onChange={({ target }) =>
            setFieldValue(['documentNumber', i], target.value)
          }
          disabled={purchaseReadOnly}
        />
      ),
    },
    {
      key: 'documentTypeId',
      dataIndex: 'documentTypeId',
      title: 'Tipo de documento',
      width: COLUMNS_WIDTH.default,
      filterDropdownOpen: filterDocTypeDropdownOpened,
      onFilterDropdownOpenChange: filterDocTypeDropdownActions.toggle,
      filterDropdown: () => (
        <DocumentTypeColumnFilter onChange={setDocumentTypeId} />
      ),
      filterIcon: <FaExchangeAlt size={14} />,
      render: (value: string, _, i) => (
        <DocumentTypesInfiniteSelectField
          value={value}
          onChange={(targetValue) => {
            setFieldValue(['documentTypeId', i], targetValue);
          }}
          allowClear
          style={{
            border: installments.every(
              (installment) => !installment.documentTypeId,
            )
              ? '1px solid red'
              : undefined,
            width: '100%',
          }}
          onDocumentTypeSelected={(documentType) => {
            if (!!documentType?.bankAccountId) {
              setFieldValue(['bankAccountId', i], documentType?.bankAccountId);
            }
          }}
          disabled={
            !hasPermissions([
              Authorities.MOVIMENTATION_PURCHASE_ADD_PAYMENT_METHODS,
            ]) || purchaseReadOnly
          }
        />
      ),
      ellipsis: true,
    },
    {
      key: 'bankAccountId',
      dataIndex: 'bankAccountId',
      title: 'Conta bancária',
      width: COLUMNS_WIDTH.default,
      filterDropdownOpen: filterBankAccountDropdownOpened,
      onFilterDropdownOpenChange: filterBankAccountDropdownActions.toggle,
      filterDropdown: () => (
        <BankAccountColumnFilter onChange={setBankAccountId} />
      ),
      filterIcon: <FaExchangeAlt size={14} />,
      render: (value: string, _, i) => (
        <BankAccountInfiniteSelectField
          queryFilter={{ active: true }}
          allowClear
          value={value}
          onChange={(targetValue) =>
            setFieldValue(['bankAccountId', i], targetValue)
          }
          style={{
            border: installments.every(
              (installment) => !installment.bankAccountId,
            )
              ? '1px solid red'
              : undefined,
            width: '100%',
          }}
          disabled={purchaseReadOnly}
        />
      ),
      ellipsis: true,
    },
    {
      dataIndex: 'dueDate',
      title: 'Vencimento',
      width: COLUMNS_WIDTH.date,
      render: (dueDate: Moment, _, i) => (
        <InputDate
          value={dueDate}
          onChange={(targetValue) => setFieldValue(['dueDate', i], targetValue)}
          disabled={purchaseReadOnly}
        />
      ),
    },
    {
      dataIndex: 'installmentValue',
      title: 'Valor da parcela',
      width: COLUMNS_WIDTH.price,
      render: (rowValue, _, i) => (
        <InputDecimal
          value={rowValue}
          onChange={(value) => {
            setFieldValue(['installmentValue', i], value);
          }}
          disabled={purchaseReadOnly}
        />
      ),
    },
  ];

  return (
    <InfiniteTable
      size="middle"
      loading={!!!installments}
      rowKey={(_row, index) => String(index)}
      dataSource={installments}
      columns={columns}
      scroll={{ x: getTableScrollX(columns) }}
    />
  );
};

export default PurchaseFinancialInstallments;
